// The board's small control icons, one set: a 16px grid, a 1.5px stroke in currentColor, so each takes the
// colour of the button it sits in (both themes) and looks the same on every system. They replace text glyphs
// (▸ ⧉ ↗ × ★ ☰) that came from whichever symbol font a system had. Always decorative: the button carries the
// name (aria-label or its text); the svg is aria-hidden. Sizes, the open-state turn and the filled pin are in
// icons.css.

import type { ReactNode } from "react";

export type IconName =
  | "chevron"
  | "chevrons"
  | "panel"
  | "goto"
  | "close"
  | "pin"
  | "remove"
  | "back"
  | "menu"
  | "plus"
  | "link"
  | "more"
  | "note"
  | "notes"
  | "notes-off"
  | "agree"
  | "question"
  | "caret"
  | "person"
  | "check"
  | "lock"
  | "people"
  | "pr"
  | "settings"
  | "search";

const SHAPES: Record<IconName, ReactNode> = {
  // ▸ show it here: points right, turns a quarter to point down while open ([aria-expanded="true"] in icons.css)
  chevron: <path d="M6 3.5 10.5 8 6 12.5" />,
  // ▸▸ show every row here
  chevrons: <path d="M3.25 3.75 7.5 8l-4.25 4.25M8.5 3.75 12.75 8 8.5 12.25" />,
  // ⧉ open in a panel: a frame with its right-hand column shaded, where the panel opens
  panel: (
    <>
      <rect x="1.75" y="2.75" width="12.5" height="10.5" rx="1.5" />
      <path d="M9.75 2.75v10.5" />
      <path className="ic-shade" d="M10.5 3.5h2.75v9H10.5z" />
    </>
  ),
  // ↗ go to it on its board
  goto: <path d="M4.75 11.25l6.5-6.5M6.25 4.75h5v5" />,
  // × close
  close: <path d="M4.25 4.25l7.5 7.5M11.75 4.25l-7.5 7.5" />,
  // a push pin; its head fills in while pinned ([aria-pressed="true"], or the "on" class)
  pin: (
    <>
      <path className="ic-fill" d="M6.25 2.25h3.5v3.75l2 3.25h-7.5l2-3.25z" />
      <path d="M5 2.25h6M8 9.25v4.5" />
    </>
  ),
  // take something off (a link, a filter): a minus in a ring
  remove: (
    <>
      <circle cx="8" cy="8" r="5.75" />
      <path d="M5.5 8h5" />
    </>
  ),
  // ‹ back one step
  back: <path d="M10 3.5 5.5 8l4.5 4.5" />,
  // ☰ the boards list
  menu: <path d="M2.75 4.25h10.5M2.75 8h10.5M2.75 11.75h10.5" />,
  // + a new item here, or a sub-item under this one
  plus: <path d="M8 3.25v9.5M3.25 8h9.5" />,
  // a link to another item (blocks, relates to, duplicates)
  link: (
    <>
      <path d="M6.9 9.1a2.6 2.6 0 0 0 3.7 0l2.1-2.1a2.6 2.6 0 0 0-3.7-3.7l-.75.75" />
      <path d="M9.1 6.9a2.6 2.6 0 0 0-3.7 0L3.3 9a2.6 2.6 0 0 0 3.7 3.7l.75-.75" />
    </>
  ),
  // … more actions
  more: (
    <>
      <circle className="ic-dot" cx="3.25" cy="8" r="1.2" />
      <circle className="ic-dot" cx="8" cy="8" r="1.2" />
      <circle className="ic-dot" cx="12.75" cy="8" r="1.2" />
    </>
  ),
  // a note (the composer, the Note chip)
  note: <path d="M3.25 3.5h9.5a1 1 0 0 1 1 1v5.75a1 1 0 0 1-1 1H7.5L4.75 13.5v-2.25h-1.5a1 1 0 0 1-1-1V4.5a1 1 0 0 1 1-1z" />,
  // notes on every card: shown, and hidden (crossed out)
  notes: (
    <>
      <path d="M3.25 3.5h9.5a1 1 0 0 1 1 1v5.75a1 1 0 0 1-1 1H7.5L4.75 13.5v-2.25h-1.5a1 1 0 0 1-1-1V4.5a1 1 0 0 1 1-1z" />
      <path d="M5.25 6.4h5.5M5.25 8.6h3.5" />
    </>
  ),
  "notes-off": (
    <>
      <path d="M3.25 3.5h9.5a1 1 0 0 1 1 1v5.75a1 1 0 0 1-1 1H7.5L4.75 13.5v-2.25h-1.5a1 1 0 0 1-1-1V4.5a1 1 0 0 1 1-1z" />
      <path d="M2.25 1.75l11.5 12.5" />
    </>
  ),
  // Agree: it happens to me too
  agree: <path d="M3.5 8.25l2.75 2.75 6.25-6.5" />,
  // Question: something about it is unclear
  question: (
    <>
      <circle cx="8" cy="8" r="5.75" />
      <path d="M6.4 6.4a1.65 1.65 0 1 1 2.3 1.55c-.45.2-.7.55-.7 1v.3M8 11.1v.05" />
    </>
  ),
  // ▾ a split button's other choices; turns over while they are open
  caret: <path d="M4.5 6.25 8 9.75l3.5-3.5" />,
  // nobody has it (the empty assignee avatar, Unassigned)
  person: (
    <>
      <circle cx="8" cy="5.6" r="2.6" />
      <path d="M3.1 13.4c.55-2.45 2.45-3.85 4.9-3.85s4.35 1.4 4.9 3.85" />
    </>
  ),
  // ✓ the one picked
  check: <path d="M3.5 8.25l2.75 2.75 6.25-6.5" />,
  // a personal board: private to the people on it
  lock: (
    <>
      <rect x="3.5" y="7.25" width="9" height="6.5" rx="1.25" />
      <path d="M5.5 7.25V5.5a2.5 2.5 0 0 1 5 0v1.75" />
    </>
  ),
  // the People screen
  people: (
    <>
      <circle cx="6" cy="5.75" r="2.25" />
      <path d="M1.75 13c.45-2.1 2.1-3.3 4.25-3.3s3.8 1.2 4.25 3.3" />
      <path d="M10.25 3.75a2.25 2.25 0 0 1 0 4.25M11.75 9.9c1.25.45 2.1 1.5 2.5 3.1" />
    </>
  ),
  // a pull request (GitHub): two commits on a branch, merging back
  pr: (
    <>
      <circle cx="4.5" cy="3.75" r="1.5" />
      <circle cx="4.5" cy="12.25" r="1.5" />
      <circle cx="11.5" cy="12.25" r="1.5" />
      <path d="M4.5 5.25v5.5M11.5 10.75V6.5a2 2 0 0 0-2-2H7.25M8.75 3 7.25 4.5l1.5 1.5" />
    </>
  ),
  // settings (the board's, a project's): a cogwheel, eight teeth
  settings: (
    <>
      <circle cx="8" cy="8" r="2" />
      <path d="M12.61 6.05L14.41 6.43L14.41 9.57L12.61 9.95L13.64 11.42L11.42 13.64L9.95 12.61L9.57 14.41L6.43 14.41L6.05 12.61L4.58 13.64L2.36 11.42L3.39 9.95L1.59 9.57L1.59 6.43L3.39 6.05L2.36 4.58L4.58 2.36L6.05 3.39L6.43 1.59L9.57 1.59L9.95 3.39L11.42 2.36L13.64 4.58Z" />
    </>
  ),
  // the quick go-to (Ctrl+/): a magnifier
  search: (
    <>
      <circle cx="7" cy="7" r="4.25" />
      <path d="M10.25 10.25l3.5 3.5" />
    </>
  ),
};

export function Icon({ name, className }: { name: IconName; className?: string }) {
  return (
    <svg
      className={`ic ic-${name}${className ? ` ${className}` : ""}`}
      viewBox="0 0 16 16"
      aria-hidden="true"
      focusable="false"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {SHAPES[name]}
    </svg>
  );
}
